import { useQuery, keepPreviousData, useQueries } from '@tanstack/react-query';
import {
  IGetFloorResourcesParameters,
  FloorService,
  FloorMarkerResourceDto,
  Floor,
  FloorCompressed,
  IGetFloorsParameters,
  ResourceFloorDto,
  IGetFloorHeatmapParameters,
} from '@jooxter/api';
import { useCallback } from 'react';
import { FloorQueryKeys } from './queryKeys';
import { FloorStaleTimeEnum } from './staleTimes';
import { AxiosError } from 'axios';

export const useFetchFloorsResourceMarkers = (
  floorIds: number[]
): {
  floorResourceMarkers: FloorMarkerResourceDto[];
  isPending: boolean;
} => {
  const fetchFloorResourceMarkers = (id: number) => FloorService.getFloorResourceMarkers(id);

  const { data: floorResourceMarkers, isPending } = useQueries({
    queries: floorIds.map((id: number) => ({
      queryKey: ['resource_marker', id],
      queryFn: () => fetchFloorResourceMarkers(id),
      placeholderData: keepPreviousData,
      staleTime: 60000,
      enabled: !!id,
    })),
    combine: (results) => {
      return {
        // one query per floor, each resolving to that floor's markers -> flatten into a single list
        data: results.flatMap((result) => result.data ?? []),
        isPending: results.some((result) => result.isPending),
      };
    },
  });

  return { floorResourceMarkers, isPending };
};

export const useFetchFloorResourceMarkers = (
  id: number,
  options?: IGetFloorResourcesParameters
): {
  floorResourceMarkers: FloorMarkerResourceDto[] | undefined;
} => {
  const fetchFloorResourceMarkers = () => FloorService.getFloorResourceMarkers(id, options);

  const { data: floorResourceMarkers } = useQuery({
    queryKey: ['resource_marker', id, options],
    queryFn: fetchFloorResourceMarkers,
    placeholderData: keepPreviousData,
    staleTime: 60000,
    refetchInterval: 30000,
  });

  return { floorResourceMarkers };
};

export const useFetchFloorPlan = (
  id?: number
): {
  floorPlan: Floor | undefined;
  isLoading: boolean;
  error?: unknown;
} => {
  const fetchFloorPlan = useCallback(() => {
    if (id) {
      return FloorService.getFloorPlan(id);
    }

    return Promise.reject(undefined);
  }, [id]);

  const {
    data: floorPlan,
    isLoading,
    error,
  } = useQuery<Floor, AxiosError, Floor>({
    queryKey: [FloorQueryKeys.GetFloorPlan, id],
    queryFn: () => fetchFloorPlan(),
    placeholderData: keepPreviousData,
    staleTime: FloorStaleTimeEnum.GetFloorPlan,
  });

  return { floorPlan, error, isLoading };
};

export const useFetchFloorPlanResources = (
  id: number,
  options?: IGetFloorResourcesParameters
): {
  resources: ResourceFloorDto[] | null | undefined;
} => {
  const fetchFloorPlanResources = () => FloorService.getFloorPlanResources(id, options);

  let enabled = true;

  if (options?.from && options?.to) {
    enabled = options.to > options.from;
  }

  const { data: resources } = useQuery({
    queryKey: [FloorQueryKeys.GetFloorPlanResources, id, options],
    queryFn: fetchFloorPlanResources,
    placeholderData: keepPreviousData,
    staleTime: FloorStaleTimeEnum.GetFloorPlanResources,
    refetchInterval: 30000,
    enabled,
  });

  return { resources };
};

export const useFetchFloorPlans = (ids: number[]) => {
  const fetchFloorPlan = (id: number) => FloorService.getFloorPlan(id);

  const { data: floorPlans, isPending } = useQueries({
    queries: ids.map((id: number) => ({
      queryKey: [FloorQueryKeys.GetFloorPlan, id],
      queryFn: () => fetchFloorPlan(id),
      placeholderData: keepPreviousData,
      staleTime: FloorStaleTimeEnum.GetFloorPlan,
      enabled: !!id,
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data).filter((r): r is Floor => !!r),
        isPending: results.some((result) => result.isPending),
      };
    },
  });

  return { floorPlans, isPending };
};

export const useFetchFloor = (
  id?: number,
  options?: { retry?: boolean | number }
): { floor?: FloorCompressed | null; isError: boolean; error: Error | null; isLoading: boolean } => {
  const fetchFloor = () => (id ? FloorService.getFloor(id) : Promise.resolve(null));

  const {
    data: floor,
    isError,
    error,
    isLoading,
  } = useQuery<FloorCompressed | null, Error, FloorCompressed | null>({
    enabled: !!id,
    refetchInterval: 30000,
    queryKey: [FloorQueryKeys.GetFloor, id, options?.retry],
    queryFn: () => fetchFloor(),
    placeholderData: keepPreviousData,
    staleTime: FloorStaleTimeEnum.GetFloor,
    retry: options?.retry,
  });

  return { floor, isError, error, isLoading };
};

export const useFetchFloors = (ids?: number[]): { floors: FloorCompressed[]; isPending: boolean } => {
  const fetchFloor = (id: number) => FloorService.getFloor(id);

  const { data: floors, isPending } = useQueries({
    queries:
      ids?.map((id: number) => ({
        queryKey: [FloorQueryKeys.GetFloor, id],
        queryFn: () => fetchFloor(id),
        placeholderData: keepPreviousData,
        staleTime: FloorStaleTimeEnum.GetFloor,
      })) ?? [],
    combine: (results) => {
      return {
        data: results.map((result) => result.data).filter((r): r is FloorCompressed => !!r),
        isPending: results.some((result) => result.isPending),
      };
    },
  });

  return { floors, isPending };
};

export const useSearchFloors = (options?: IGetFloorsParameters, enabled = true) => {
  const fetchFloors = () => FloorService.getFloorsWithPagination(options);

  const { data: floors } = useQuery({
    queryKey: [FloorQueryKeys.GetFloorsSearch, options],
    queryFn: () => fetchFloors(),
    staleTime: FloorStaleTimeEnum.GetFloorsSearch,
    enabled,
  });

  return { floors };
};

export const useFetchHeatmap = (id: number, options: IGetFloorHeatmapParameters, enabled: boolean) => {
  const {
    data: heatmapData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [FloorQueryKeys.GetFloorPlanHeatmap, id, options],
    queryFn: () => FloorService.getFloorPlanHeatmap(id, options),
    enabled,
  });

  return { heatmapData, isLoading, error };
};
